// Float32 pixel buffer helpers (DOM-independent).
// Blockers #1: All internal channels 0..1 floats. SRGB is 0..1 (not 0..255).
// 0..255 only at Uint8 boundaries. No silent conversions; getPixel always
// returns linear-light RGBLinear. fromUint8Array/toUint8Array convert explicitly.

import type { PixelBuffer, ColorSpace, SRGB, RGBLinear } from '../types';
import {
  srgbToLinear,
  linearToSrgb,
  srgbToRgbLinear,
  rgbLinearToSrgb,
} from '../color/spaces';

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function createBuffer(
  width: number,
  height: number,
  colorSpace: ColorSpace = 'srgb'
): PixelBuffer {
  return {
    width,
    height,
    colorSpace,
    data: new Float32Array(width * height * 4),
  };
}

/**
 * Get pixel as linear-light RGBLinear. Converts from sRGB if buffer is sRGB.
 * Never silently returns sRGB as linear (blocker #2).
 */
export function getPixel(buf: PixelBuffer, x: number, y: number): RGBLinear {
  const i = (y * buf.width + x) * 4;
  if (buf.colorSpace === 'srgb') {
    return {
      r: srgbToLinear(buf.data[i]),
      g: srgbToLinear(buf.data[i + 1]),
      b: srgbToLinear(buf.data[i + 2]),
    };
  }
  return {
    r: buf.data[i],
    g: buf.data[i + 1],
    b: buf.data[i + 2],
  };
}

/**
 * Set pixel from linear-light RGBLinear. Converts to sRGB if buffer is sRGB.
 */
export function setPixel(buf: PixelBuffer, x: number, y: number, c: RGBLinear): void {
  const i = (y * buf.width + x) * 4;
  if (buf.colorSpace === 'srgb') {
    buf.data[i] = linearToSrgb(c.r);
    buf.data[i + 1] = linearToSrgb(c.g);
    buf.data[i + 2] = linearToSrgb(c.b);
  } else {
    buf.data[i] = c.r;
    buf.data[i + 1] = c.g;
    buf.data[i + 2] = c.b;
  }
  buf.data[i + 3] = 1;
}

/**
 * From Uint8ClampedArray (0..255).
 * - colorSpace 'srgb': store arr/255 directly (sRGB encoded, 0..1).
 * - colorSpace 'linear': store srgbToLinear(arr/255) (convert to linear light).
 * Blocker #1: fromUint8Array with 'linear' must actually convert.
 */
export function fromUint8Array(
  arr: Uint8ClampedArray,
  w: number,
  h: number,
  colorSpace: ColorSpace = 'srgb'
): PixelBuffer {
  const buf = createBuffer(w, h, colorSpace);
  const n = w * h * 4;
  if (colorSpace === 'srgb') {
    for (let i = 0; i < n; i++) {
      buf.data[i] = arr[i] / 255;
    }
  } else {
    // 'linear': convert sRGB-encoded input to linear light
    for (let i = 0; i < n; i += 4) {
      buf.data[i] = srgbToLinear(arr[i] / 255);
      buf.data[i + 1] = srgbToLinear(arr[i + 1] / 255);
      buf.data[i + 2] = srgbToLinear(arr[i + 2] / 255);
      buf.data[i + 3] = arr[i + 3] / 255;
    }
  }
  return buf;
}

/**
 * To Uint8ClampedArray (0..255).
 * - colorSpace 'srgb': write round(clamp01(v)*255).
 * - colorSpace 'linear': write round(clamp01(linearToSrgb(v))*255).
 * No intermediate rounding before the final Uint8 boundary.
 */
export function toUint8Array(buf: PixelBuffer): Uint8ClampedArray {
  const out = new Uint8ClampedArray(buf.width * buf.height * 4);
  const n = buf.width * buf.height;
  if (buf.colorSpace === 'srgb') {
    for (let i = 0; i < n; i++) {
      const di = i * 4;
      out[di] = Math.round(clamp01(buf.data[di]) * 255);
      out[di + 1] = Math.round(clamp01(buf.data[di + 1]) * 255);
      out[di + 2] = Math.round(clamp01(buf.data[di + 2]) * 255);
      out[di + 3] = Math.round(clamp01(buf.data[di + 3]) * 255);
    }
  } else {
    for (let i = 0; i < n; i++) {
      const di = i * 4;
      out[di] = Math.round(clamp01(linearToSrgb(buf.data[di])) * 255);
      out[di + 1] = Math.round(clamp01(linearToSrgb(buf.data[di + 1])) * 255);
      out[di + 2] = Math.round(clamp01(linearToSrgb(buf.data[di + 2])) * 255);
      out[di + 3] = Math.round(clamp01(buf.data[di + 3]) * 255);
    }
  }
  return out;
}
